import React, { useEffect, useMemo, useState } from "react";
import MaterialReactTable, {
  MRT_PaginationState,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
} from "material-react-table";
import {
  Eye16Filled,
  Attach20Regular,
  Edit16Filled,
  Copy20Regular,
  FolderOpenRegular,
} from "@fluentui/react-icons";
import { useAssystFilePicker } from "./ElectronAppFiles/useAssystFilePicker";
import AssystLinkModal from "./ElectronAppFiles/AssystLinkModal";
import { useToastMessage } from "./ToastMessage/ToastMessage";
import {
  Button,
  makeStyles,
  Tooltip,
  Toaster,
  Spinner,
  shorthands,
} from "@fluentui/react-components";
import {
  formatedDate,
  formattedDuration,
  formattedTime,
  getClientNoteCategories,
  getClientType,
  getYes_NoConversion,
  stripHtmlTags,
} from "../utils";
import ReactQuillComponent from "./ReactQuillComponent";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import apiServices from "../service";
import ASCButton from "./Button";
import MultiLineText from "./TableMultiLineData/MultiLineData";
import HighlightText from "./HighlightText/index";
import { ObjectiveKeyMapping, transformKeys } from "../utils/DataKeyMapping";
import {
  findRowIndexByKey,
  getContextIdKey,
  getRowKey,
  useRestoreRowSelection,
} from "./tableRowSelection";
import { withCurrencyColumns } from "../components/Common/currencyColumn";
const useStyles = makeStyles({
  wrapper: {
    columnGap: "15px",
    display: "flex",
    cursor: "pointer",
  },
  contentHeader: {
    marginTop: "0",
  },
  scopeArea: {
    display: "grid",
    gridTemplateRows: "repeat(1fr)",
    justifyItems: "start",
    ...shorthands.gap("2px"),
    maxWidth: "400px",
  },
});

type ComponentProps = {
  style?: React.CSSProperties;
  [key: string]: any;
};

const downloadFile = (documentId: string, customerId: string) => {
  if (!documentId) return;

  apiServices.document
    .getDocument(documentId, customerId)
    .then((res: any) => {
      if (res.data && res.data.data) {
        const url = res.data.data;
        const link = document.createElement("a");
        link.href = url;
        link.download = "document"; // You can set a default name for the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    })
    .catch((err) => {
      console.error("Error downloading file:", err);
    });
};

function FileLinkActions({
  path,
  documentId,
  customerId,
}: {
  path: string;
  documentId?: string;
  customerId?: string;
}) {
  const {
    openFile,
    isLoading,
    platform,
    installerName,
    downloadUrl,
    latestVersion,
    needsUpdate,
  } = useAssystFilePicker();
  const { showToast, toasterId } = useToastMessage();
  const [copied, setCopied] = useState(false);
  const [assystLinkOpen, setAssystLinkOpen] = useState(false);

  const resolveFilePath = async (): Promise<string> => {
    if (path) return path;
    if (!documentId || !customerId) return "";
    try {
      const res = await apiServices.document.getDocument(
        documentId,
        customerId,
      );
      const doc = res.data?.data;
      return doc?.fileUrl || doc?.FileLink || "";
    } catch {
      return "";
    }
  };

  const handleCopy = async () => {
    const filePath = await resolveFilePath();
    if (!filePath) return;
    const write = (text: string) => {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      } else {
        const el = document.createElement("textarea");
        el.value = text;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    };
    write(filePath);
  };

  const handleOpen = async () => {
    const filePath = await resolveFilePath();
    if (!filePath) {
      setAssystLinkOpen(true);
      return;
    }
    await openFile(filePath, {
      onNotInstalled: () => setAssystLinkOpen(true),
      onError: (message) => showToast(message, "", "error"),
    });
  };

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" />
      <span
        style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
      >
        {isLoading ? (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Spinner size="extra-tiny" />
            <span style={{ fontSize: 11, color: "#475569" }}>Opening…</span>
          </span>
        ) : (
          <>
            <Tooltip
              content={copied ? "Copied!" : "Copy path"}
              relationship="description"
            >
              <span
                className="filelink-info-icon"
                role="button"
                aria-label="Copy"
              >
                <Copy20Regular onClick={handleCopy} />
              </span>
            </Tooltip>
            <Tooltip content="Open in file explorer" relationship="description">
              <span
                className="filelink-info-icon"
                role="button"
                onClick={handleOpen}
              >
                <FolderOpenRegular />
              </span>
            </Tooltip>
          </>
        )}
      </span>
      <AssystLinkModal
        open={assystLinkOpen}
        onClose={() => setAssystLinkOpen(false)}
        needsUpdate={needsUpdate}
        latestVersion={latestVersion}
        platform={platform}
        installerName={installerName}
        downloadUrl={downloadUrl}
      />
    </>
  );
}

const tableColumnList = {
  CLIENT_EMPLOYMENT: [
    {
      accessorKey: "occupation",
      header: "Occupation",
    },
    {
      accessorKey: "employment_type",
      header: "Employment Type",
    },
    {
      accessorKey: "employment_basis",
      header: "Employment Basis",
    },
    {
      accessorKey: "principal_employment",
      header: "Principal Employment ",
    },
    {
      accessorKey: "employer_name",
      header: "Employer Name",
    },
    {
      accessorKey: "address_1",
      header: "Employer Address",
    },

    {
      accessorKey: "town",
      header: "Town",
    },
    {
      accessorKey: "county",
      header: "County",
    },
    {
      accessorKey: "country",
      header: "Country",
    },
    {
      accessorKey: "postcode",
      header: "Post Code",
    },
    {
      accessorKey: "phone",
      header: "Phone",
    },
    {
      accessorKey: "fax",
      header: "Fax",
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "salary_review_date",
      header: "Salary Review",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.salary_review_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "auto_enrolment",
      header: "Automatic Entrolment",
    },
    {
      accessorKey: "retirement_age",
      header: "Retirement Age",
    },
    {
      accessorKey: "position",
      header: "Position",
    },
    {
      accessorKey: "payroll_id",
      header: "Payroll Id",
    },
    {
      accessorKey: "controlling_director",
      header: "Controlling Director?",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getYes_NoConversion(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getYes_NoConversion(row.original.controlling_director)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "shareholding",
      header: "Shareholding",
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
    {
      accessorKey: "death_in_service",
      header: "Death In Service",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const display =
          `${getYes_NoConversion(row.original.death_in_service_yn)} ${row.original.death_in_service || ""}`.trim();
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={`${getYes_NoConversion(row.original.death_in_service_yn)} ${row.original.death_in_service || ""}`.trim()}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "sick_pay",
      header: "Sick Pay",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const display =
          `${getYes_NoConversion(row.original.sick_pay_yn)} ${row.original.sick_pay || ""}`.trim();
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={`${getYes_NoConversion(row.original.sick_pay_yn)} ${row.original.sick_pay || ""}`.trim()}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "pension",
      header: "Pension",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const display =
          `${getYes_NoConversion(row.original.pension_yn)} ${row.original.pension || ""}`.trim();
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={`${getYes_NoConversion(row.original.pension_yn)} ${row.original.pension || ""}`.trim()}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "pmi",
      header: "PMI",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const display =
          `${getYes_NoConversion(row.original.pmi_yn)} ${row.original.pmi || ""}`.trim();
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={`${getYes_NoConversion(row.original.pmi_yn)} ${row.original.pmi || ""}`.trim()}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "phi",
      header: "PHI",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const display =
          `${getYes_NoConversion(row.original.phi_yn)} ${row.original.phi || ""}`.trim();
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={`${getYes_NoConversion(row.original.phi_yn)} ${row.original.phi || ""}`.trim()}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "critical_illness",
      header: "Critical Illness",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const display =
          `${getYes_NoConversion(row.original.critical_illness_yn)} ${row.original.critical_illness || ""}`.trim();
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={`${getYes_NoConversion(row.original.critical_illness_yn)} ${row.original.critical_illness || ""}`.trim()}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "company_car",
      header: "Company Car",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const display =
          `${getYes_NoConversion(row.original.company_car_yn)} ${row.original.company_car || ""}`.trim();
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={`${getYes_NoConversion(row.original.company_car_yn)} ${row.original.company_car || ""}`.trim()}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "share_options",
      header: "Share Options",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const display =
          `${getYes_NoConversion(row.original.share_options_yn)} ${row.original.share_options || ""}`.trim();
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={`${getYes_NoConversion(row.original.share_options_yn)} ${row.original.share_options || ""}`.trim()}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "other",
      header: "Other Options",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const display =
          `${getYes_NoConversion(row.original.other_yn)} ${row.original.other || ""}`.trim();
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={`${getYes_NoConversion(row.original.other_yn)} ${row.original.other || ""}`.trim()}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
  ],

  CLIENT_ATTITUDE_TO_RISK: [
    {
      accessorKey: "category",
      header: "Category",
    },
    // {
    //   accessorKey: "category_description",
    //   header: "Category Description",
    // },
    {
      accessorKey: "rating",
      header: "Rating",
    },
    // {
    //   accessorKey: "rating_description",
    //   header: "Rating Description",
    // },
    {
      accessorKey: "score",
      header: "Score",
    },
  ],
  CLIENT_IDENTITY: [
    {
      accessorKey: "category",
      header: "Category",
    },
    {
      accessorKey: "item",
      header: "Item",
    },
    {
      accessorKey: "reference",
      header: "Reference",
    },
    {
      accessorKey: "issued",
      header: "Issued",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.issued)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "expires",
      header: "Expires",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.expires)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "issued_at",
      header: "Issued at",
    },
    {
      accessorKey: "seen",
      header: "Seen",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.seen)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
  ],
  CLIENT_OBJECTIVES: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "objective_description",
      header: "Objective",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.objective_description} />
        </>
      ),
    },
    {
      accessorKey: "applicable",
      header: "Applicable",
      filterFn: (row: any, columnId: any, filterValue: any) => {
        const display = row.getValue(columnId) === 1 ? "Yes" : "No";
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={row.original.applicable === 1 ? "Yes" : "No"}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },

    {
      accessorKey: "rating",
      header: "Rating",
    },
    {
      accessorKey: "comments",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.comments} />
        </>
      ),
    },
  ],
  CLIENT_SERVICE_TYPE: [
    {
      accessorKey: "description",
      header: "Service Type",
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },

    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
  ],
  COMMISSION_SERVICE_TYPE: [
    {
      accessorKey: "description",
      header: "Service Type",
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },

    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
  ],
  COMMISSION_PARTNER_SERVICE_TYPE: [
    {
      accessorKey: "description",
      header: "Service Type",
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },

    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
  ],
  CLIENT_INCOME: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "owner",
      header: "Owner",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getClientType(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getClientType(row.original.owner)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "income_type",
      header: "Type",
    },
    {
      accessorKey: "gross_amount",
      header: "Gross Amount",
    },
    {
      accessorKey: "net_amount",
      header: "Net Amount",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
    {
      accessorKey: "source",
      header: "Source",
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  CLIENT_DEPENDANT: [
    {
      accessorKey: "title",
      header: "Title",
    },
    {
      accessorKey: "first_names",
      header: "First Name(s)",
    },
    {
      accessorKey: "last_name",
      header: "Last Name",
    },
    {
      accessorKey: "known_as",
      header: "Known as",
    },
    {
      accessorKey: "sex",
      header: "Sex",
    },
    {
      accessorKey: "date_of_birth",
      header: "Date of Birth",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.date_of_birth)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    // {
    //   accessorKey: "dependants.age_next",
    //   header: "Age Next",
    // },
    {
      accessorKey: "relation",
      header: "Relation to Client",
    },
    {
      accessorKey: "parent",
      header: "Parent",
    },
    {
      accessorKey: "smoker",
      header: "Smoker",
    },
    {
      accessorKey: "living_at_home",
      header: "Living at Home",
    },
    {
      accessorKey: "financially_dependant",
      header: "Financially Dependant",
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  CLIENT_OUTGOINGS: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
    {
      accessorKey: "discretionary",
      header: "Type",
    },

    {
      accessorKey: "client_amount",
      header: "Client Amount",
    },
    {
      accessorKey: "partner_amount",
      header: "Partner Amount",
    },
    {
      accessorKey: "joint_amount",
      header: "Joint Amount",
    },
  ],
  ADDRESS_BOOK: [
    {
      accessorKey: "address_type",
      header: "Address Type",
    },
    {
      accessorKey: "organisation",
      header: "Organisation",
    },
    {
      accessorKey: "contact_name",
      header: "Contact Name",
    },
    {
      accessorKey: "address_1",
      header: "Address",
      filterFn: (row: any, _columnId: any, filterValue: any) => {
        const combined = [
          row.original.address_1,
          row.original.address_2,
          row.original.address_3,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return combined.includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <MultiLineText
          lines={[
            row.original.address_1,
            row.original.address_2,
            row.original.address_3,
          ]}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "town",
      header: "Town",
    },
    {
      accessorKey: "county",
      header: "County",
    },
    {
      accessorKey: "country",
      header: "Country",
    },
    {
      accessorKey: "postcode",
      header: "Postcode",
    },
    {
      accessorKey: "telephone",
      header: "Telephone",
    },
    {
      accessorKey: "home_phone",
      header: "Home Phone",
    },
    {
      accessorKey: "work_phone",
      header: "Work Phone",
    },
    {
      accessorKey: "mobile",
      header: "Mobile",
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "work_email",
      header: "Work Email",
    },
    {
      accessorKey: "effective_date",
      header: "Effective Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.effective_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  CONTACTS_NOTES: [
    {
      accessorKey: "note_date",
      header: "Date",
      size: 110,
      maxSize: 110,
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.note_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "note_time",
      header: "Time",
      size: 90,
      maxSize: 90,
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formattedTime(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formattedTime(row.original.note_time)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "note_type",
      header: "Type",
      size: 100,
      maxSize: 100,
    },

    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
    {
      accessorKey: "category",
      header: "Category",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getClientNoteCategories(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getClientNoteCategories(row.original.category)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "file_link",
      header: "Link",
      Cell: ({ row }: any) => {
        return (
          <div>
            {row.original.file_source === "REFERENCE" ? (
              <FileLinkActions
                path={
                  row.original.attachments?.[0]?.FileLink ||
                  row.original.attachments?.[0]?.fileUrl ||
                  ""
                }
                documentId={row.original.file_link}
                customerId={row.original.customer_id}
              />
            ) : (row.original.file_source === "UPLOAD" &&
                (row.original.file_link !== "" ||
                  row.original.file_link !== null)) ||
              (row.original.attachment_file != undefined &&
                row.original.attachment_file !== null &&
                row.original.attachment_file != "") ? (
              <span>
                <Attach20Regular />
              </span>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "created_by_name",
      header: "Created By",
      Cell: ({ row }: any) => <>{row.original.created_by_name ?? "-"}</>,
    },
  ],
  FACT_FIND_NOTES: [
    {
      accessorKey: "category",
      header: "Category",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getClientNoteCategories(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getClientNoteCategories(row.original.category)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "note_date",
      header: "Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.note_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "note_time",
      size: 90,
      maxSize: 90,
      header: "Time",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formattedTime(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formattedTime(row.original.note_time)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "note_type",
      header: "Type",
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
    {
      accessorKey: "file_link",
      header: "File Link",
      Cell: ({ row }: any) => {
        return (
          <div>
            {row.original.file_source === "REFERENCE" ? (
              <FileLinkActions
                path={
                  row.original.attachments?.[0]?.FileLink ||
                  row.original.attachments?.[0]?.fileUrl ||
                  row.original.referencePath ||
                  ""
                }
                documentId={row.original.file_link}
                customerId={row.original.customer_id}
              />
            ) : (row.original.file_source === "UPLOAD" &&
                (row.original.file_link !== "" ||
                  row.original.file_link !== null)) ||
              (row.original.attachment_file != undefined &&
                row.original.attachment_file !== null &&
                row.original.attachment_file != "") ? (
              <span>
                <Attach20Regular />
              </span>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },
  ],
  CONTACTS_TIME_ALLOCATION: [
    {
      accessorKey: "allocated_date",
      header: "Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.allocated_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "duration",
      header: "Duration",
      // Cell: ({ row }: any) => <>{formattedDuration(row.original.duration)}</>,
      Cell: ({ row }: any) => <>{formattedDuration(row.original.duration)}</>, // Custom rendering
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
    {
      accessorKey: "resource",
      header: "Resource",
    },
  ],
  CONTACTS_CLIENT_ACTION: [
    {
      accessorKey: "appointment_date",
      header: "Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.appointment_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "appointment_time",
      header: "Time",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formattedTime(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formattedTime(row.original.appointment_time)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "appointment_type",
      header: "Type",
    },
    {
      accessorKey: "appointment_note",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.appointment_note} />
        </>
      ),
    },
    {
      accessorKey: "appointment_assigned",
      header: "Assigned",
    },
    {
      accessorKey: "appointment_status",
      header: "Status",
    },
  ],
  FEES_CHARGES: [
    {
      accessorKey: "commission_type",
      header: "Fee Type",
    },
    {
      accessorKey: "payment_source",
      header: "Source of Payment",
    },
    {
      accessorKey: "amount_to_company",
      header: "Amount to Company",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "payment_count",
      header: "Number of Payments",
    },
    {
      accessorKey: "consultant",
      header: "Consultant",
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  PARTNER_EMPLOYMENT: [
    {
      accessorKey: "occupation",
      header: "Occupation",
    },
    {
      accessorKey: "employment_type",
      header: "Employment Type",
    },
    {
      accessorKey: "employment_basis",
      header: "Employment Basis",
    },
    {
      accessorKey: "principal_employment",
      header: "Principal Employment ",
    },
    {
      accessorKey: "employer_name",
      header: "Employer Name",
    },
    {
      accessorKey: "address_1",
      header: "Employer Address",
    },

    {
      accessorKey: "town",
      header: "Town",
    },
    {
      accessorKey: "county",
      header: "County",
    },
    {
      accessorKey: "country",
      header: "Country",
    },
    {
      accessorKey: "postcode",
      header: "Post Code",
    },
    {
      accessorKey: "phone",
      header: "Phone",
    },
    {
      accessorKey: "fax",
      header: "Fax",
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "salary_review_date",
      header: "Salary Review",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.salary_review_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "auto_enrolment",
      header: "Automatic Entrolment",
    },
    {
      accessorKey: "retirement_age",
      header: "Retirement Age",
    },
    {
      accessorKey: "position",
      header: "Position",
    },
    {
      accessorKey: "payroll_id",
      header: "Payroll Id",
    },
    {
      accessorKey: "controlling_director",
      header: "Controlling Director?",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getYes_NoConversion(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getYes_NoConversion(row.original.controlling_director)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "shareholding",
      header: "Shareholding",
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
    {
      accessorKey: "death_in_service",
      header: "Death In Service",
    },
    {
      accessorKey: "sick_pay",
      header: "Sick Pay",
    },
    {
      accessorKey: "pension",
      header: "Pension",
    },
    {
      accessorKey: "pmi",
      header: "PMI",
    },
    {
      accessorKey: "phi",
      header: "PHI",
    },
    {
      accessorKey: "critical_illness",
      header: "Critical Illness",
    },
    {
      accessorKey: "company_car",
      header: "Company Car",
    },
    {
      accessorKey: "share_options",
      header: "Share Options",
    },
    {
      accessorKey: "other",
      header: "Other Options",
    },
  ],
  PARTNER_ATTITUDE_TO_RISK: [
    {
      accessorKey: "category",
      header: "Category",
    },
    {
      accessorKey: "category_description",
      header: "Category Description",
    },
    {
      accessorKey: "rating",
      header: "Rating",
    },
    {
      accessorKey: "rating_description",
      header: "Rating Description",
    },
  ],
  PARTNER_IDENTITY: [
    {
      accessorKey: "category",
      header: "Category",
    },
    {
      accessorKey: "item",
      header: "Item",
    },
    {
      accessorKey: "reference",
      header: "Reference",
    },
    {
      accessorKey: "issued",
      header: "Issued",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.issued)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "expires",
      header: "Expires",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.expires)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "issued_at",
      header: "Issued at",
    },
    {
      accessorKey: "seen",
      header: "Seen",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.seen)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
  ],
  PARTNER_OBJECTIVES: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "objective_description",
      header: "Objective",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.objective_description} />
        </>
      ),
    },
    {
      accessorKey: "applicable",
      header: "Applicable",
      filterFn: (row: any, columnId: any, filterValue: any) => {
        const display = row.getValue(columnId) === 1 ? "Yes" : "No";
        return display
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase());
      },
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={row.original.applicable === 1 ? "Yes" : "No"}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "rating",
      header: "Rating",
    },
    {
      accessorKey: "comments",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.comments} />
        </>
      ),
    },
  ],
  PARTNER_SERVICE_TYPE: [
    {
      accessorKey: "description",
      header: "Service Type",
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },

    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
  ],
  PARTNER_INCOME: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "owner",
      header: "Owner",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getClientType(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getClientType(row.original.owner)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "income_type",
      header: "Type",
    },
    {
      accessorKey: "gross_amount",
      header: "Gross",
    },
    {
      accessorKey: "net_amount",
      header: "Net",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
    {
      accessorKey: "source",
      header: "Source",
    },
  ],
  PARTNER_DEPENDANT: [
    {
      accessorKey: "title",
      header: "Title",
    },
    {
      accessorKey: "first_names",
      header: "First Name(s)",
    },
    {
      accessorKey: "last_name",
      header: "Last Name",
    },
    {
      accessorKey: "known_as",
      header: "Known as",
    },
    {
      accessorKey: "sex",
      header: "Sex",
    },
    {
      accessorKey: "date_of_birth",
      header: "Date of Birth",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.date_of_birth)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    // {
    //   accessorKey: "dependants.age_next",
    //   header: "Age Next",
    // },
    {
      accessorKey: "relation",
      header: "Relation to Client",
    },
    {
      accessorKey: "parent",
      header: "Parent",
    },
    {
      accessorKey: "smoker",
      header: "Smoker",
    },
    {
      accessorKey: "living_at_home",
      header: "Living at Home",
    },
    {
      accessorKey: "financially_dependant",
      header: "Financially Dependant",
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  PARTNER_OUTGOINGS: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "description",
      header: "Outgoing",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
    {
      accessorKey: "discretionary",
      header: "Type",
    },

    {
      accessorKey: "client_amount",
      header: "Client Amount",
    },
    {
      accessorKey: "partner_amount",
      header: "Partner Amount",
    },
    {
      accessorKey: "joint_amount",
      header: "Joint Amount",
    },
  ],
  MASTER_SETTINGS_INCOME_CATEGORY: [
    {
      accessorKey: "sort_order",
      header: "Sort Order",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
  ],
};

const TableDataList = (props: ComponentProps) => {
  const styles = useStyles();
  const [rowCount, setRowCount] = useState(0);
  const [count, setCount] = useState(0);

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [objectiveData, setObjectiveData] = useState<any[]>([]);
  const { clientId, caseId, action, selectedItemId, itemId, userId } =
    useParams();
  const tableRef = React.useRef<HTMLDivElement>(null);
  // Set while the selection is being restored from the URL, so the effect
  // below can tell it apart from a user clicking a row.
  const restoringSelectionRef = React.useRef(false);
  const idKey = getContextIdKey(props.context);
  // Route params aren't named consistently across modules (`:selectedItemId`
  // for client sections, `:itemId`/`:userId` elsewhere), so a grid that reads
  // one fixed name silently restores nothing on the others. An explicit
  // `selectedId` prop always wins.
  const selectedId =
    props.selectedId ?? selectedItemId ?? itemId ?? userId ?? undefined;
  const columns = useMemo<MRT_ColumnDef<{ [key: string]: any }>[]>(
    () => [],
    [],
  );
  const customColumns = useMemo<MRT_ColumnDef<{ [key: string]: any }>[]>(() => {
    if (props.context !== "") {
      return withCurrencyColumns(
        (tableColumnList as { [key: string]: any })[props.context] ?? [],
        props.context,
      );
    }
    return [];
  }, [props.context]);
  React.useEffect(() => {
    // A selection restored from the URL must not re-fire the row action: it
    // navigates, so it would rewrite `/edit` back to `/view` and push a
    // history entry every time the page is refreshed.
    if (restoringSelectionRef.current) {
      restoringSelectionRef.current = false;
      return;
    }
    if (
      props.handleRowAction !== undefined &&
      props.data.length > 0 &&
      Object.keys(rowSelection)[0] !== undefined
    ) {
      const index = findRowIndexByKey(
        props.data,
        Object.keys(rowSelection)[0],
        idKey,
      );
      const selectedRow = index < 0 ? undefined : props.data[index];
      if (selectedRow) {
        props.handleRowAction(
          props.isContactHistory ? "edit" : "view",
          selectedRow,
          index + 1,
        );
      }
    }
  }, [rowSelection]);

  // useEffect(() => {
  //   if (props.context === "CLIENT_OBJECTIVES") {
  //     apiServices.client
  //       .getDetailByTab(
  //         props.customerId,
  //         "objectives",
  //         props.pagination.pageIndex + 1,
  //         props.pagination.pageSize
  //       )
  //       .then((response) => {
  //         const transformedData = transformKeys(
  //           response.data.data.list,
  //           ObjectiveKeyMapping
  //         );
  //         setObjectiveData(transformedData);
  //         setRowCount(response.data.data.count);
  //       });
  //   }
  // }, [props.pagination.pageIndex, props.pagination.pageSize]);

  // Re-apply the selection whenever the id or the list changes. On a refresh
  // the id is known on the first render but the rows only arrive with the
  // response, so an effect that does not depend on `props.data` restores nothing.
  useRestoreRowSelection({
    data: props.data,
    idKey,
    selectedId,
    rowSelection,
    setRowSelection,
    setCurrentRecord: props.setCurrentRecord,
    onBeforeRestore: () => {
      restoringSelectionRef.current = true;
    },
    containerRef: tableRef,
  });

  useEffect(() => {
    if (props.isContactHistory) {
      if (
        props.selectedRowItem &&
        Object.keys(props.selectedRowItem).length !== 0 &&
        props.data.length > 0
      ) {
        let index = 0;
        if (props.context === "CONTACTS_NOTES") {
          index = props.data.findIndex((x: any) => x.note_id === props.tableId);
        }
        if (index < 0) return;
        setRowSelection({ [getRowKey(props.data[index], index, idKey)]: true });
        props.setCurrentRecord?.(index + 1);
      }
    }
  }, []);

  return (
    <div>
      {customColumns && customColumns.length > 0 && (
        <div ref={tableRef}>
          <MaterialReactTable
            columns={customColumns}
            data={props.data}
            enableRowSelection
            enableColumnActions={false}
            onRowSelectionChange={setRowSelection}
            // enableRowNumbers
            enableBottomToolbar={props.context === "CLIENT_OBJECTIVES"}
            positionToolbarAlertBanner="none"
            rowNumberMode="static"
            enableMultiRowSelection={false}
            muiTableBodyRowProps={({ row }) => ({
              onClick: row.getToggleSelectedHandler(),
              sx: { cursor: "pointer" },
            })}
            getRowId={(row, index) => getRowKey(row, index, idKey)}
            enableRowActions={false}
            displayColumnDefOptions={{
              "mrt-row-actions": {
                header: "Actions", //change header text
                // size: 300, //make actions column wider
                muiTableHeadCellProps: {
                  align: "center",
                },
                muiTableBodyCellProps: {
                  align: "center",
                },
              },
            }}
            renderRowActions={({ row, table }) => (
              <div className={styles.wrapper}>
                <Tooltip
                  content="View"
                  relationship="label"
                  positioning="below"
                >
                  <ASCButton
                    shape="rounded"
                    appearance="subtle"
                    icon={
                      <Eye16Filled
                        primaryFill="var(--button-color)"
                        onClick={() => {
                          props.handleRowAction("view", row);
                        }}
                      />
                    }
                  />
                </Tooltip>
                <Tooltip
                  content="Edit"
                  relationship="label"
                  positioning="below"
                >
                  <ASCButton
                    shape="rounded"
                    appearance="subtle"
                    icon={
                      <Edit16Filled
                        primaryFill="var(--button-color)"
                        onClick={() => {
                          props.handleRowAction("edit", row);
                        }}
                      />
                    }
                  />
                </Tooltip>
              </div>
            )}
            enablePagination={props.context === "CLIENT_OBJECTIVES"}
            initialState={{
              density: "compact",
              columnPinning: { right: ["mrt-row-actions"] },
            }}
            positionActionsColumn="last"
            state={{
              rowSelection,
              ...(props.context === "CLIENT_OBJECTIVES" && props.pagination
                ? { pagination: props.pagination }
                : {}),
            }}
            {...(props.context === "CLIENT_OBJECTIVES" && {
              manualPagination: true,
              onPaginationChange: props.setPagination,
              rowCount: props.rowCount,
            })}
          />
        </div>
      )}
    </div>
  );
};

export default TableDataList;
